/**
 * DataList table component.
 *
 * Renders the main table with:
 * - Column headers with sort indicators and optional header tooltips
 * - Data rows via DataListRow (desktop) or DataListMobileCard (mobile)
 * - Skeleton rows during initial loading
 * - DataListEmpty when no data is present
 * - Select-all checkbox when row selection is enabled
 * - Footer row with column totals when configured
 * - Action column header when row actions exist
 * - Responsive: desktop shows Table, mobile (<md) shows cards
 * - Returns null when the table is collapsed
 */

import { Table } from '../../../base/data/Table/Table';
import { Checkbox } from '../../../base/inputs/Checkbox/Checkbox';
import { Skeleton } from '../../../base/feedback/Skeleton/Skeleton';
import { Alert } from '../../../base/feedback/Alert/Alert';
import { Tooltip } from '../../../base/display/Tooltip/Tooltip';
import { Text } from '../../../base/display/Text/Text';

import { DataListRow } from './DataListRow';
import { DataListMobileCard } from './DataListMobileCard';
import { DataListEmpty } from './DataListEmpty';
import { useDataListContext } from '../context/DataListContext';
import { DEFAULT_I18N_KEYS } from '../constants';
import type { ReactNode } from 'react';
import type { ColumnConfig, DataListTableProps } from '../types';
import { safeString } from '../utils/cellRenderer';
import { formatColumnTotal } from '../utils/totals';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Number of skeleton rows shown during initial loading. */
const SKELETON_ROW_COUNT = 5;

/** Stable keys for skeleton rows to avoid using array indices as React keys. */
const SKELETON_KEYS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => `skeleton-${i}`);

// ---------------------------------------------------------------------------
// DataListTable Component
// ---------------------------------------------------------------------------

export function DataListTable({ className }: Readonly<DataListTableProps>) {
  const { config, list } = useDataListContext();

  const isSelectable =
    config.actions?.selectable ||
    (config.actions?.bulkActions?.length ?? 0) > 0;
  const hasRowActions = (config.actions?.rowActions?.length ?? 0) > 0;
  const sortingEnabled = config.sorting?.enabled !== false;

  // When collapsed, hide the table entirely
  if (list.isCollapsed) {
    return null;
  }

  // Loading skeleton state (only for initial load)
  if (list.isInitialLoading) {
    return (
      <div className={className} data-testid="datalist-table-loading">
        <Table
          size={config.display?.size ?? 'default'}
          className={config.cssOverrides?.table}
        >
          <Table.Head>
            <Table.Row>
              {isSelectable && <Table.HeaderCell width={48} />}
              {list.visibleColumns.map((col) => (
                <Table.HeaderCell key={col.key}>{col.label}</Table.HeaderCell>
              ))}
              {hasRowActions && <Table.HeaderCell width={100} />}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {SKELETON_KEYS.map((skeletonKey) => (
              <Table.Row key={skeletonKey}>
                {isSelectable && (
                  <Table.Cell>
                    <Skeleton variant="text" />
                  </Table.Cell>
                )}
                {list.visibleColumns.map((col) => (
                  <Table.Cell key={col.key}>
                    <Skeleton variant="text" />
                  </Table.Cell>
                ))}
                {hasRowActions && (
                  <Table.Cell>
                    <Skeleton variant="text" />
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  // Error state — a failed fetch must not look like "no data".
  // Empty state only when the fetch actually succeeded.
  if (list.data.length === 0 && !list.isLoading) {
    if (list.error) {
      return (
        <div className={className} data-testid="datalist-table-error">
          <Alert
            variant="error"
            title={config.i18n?.errorMessage ?? DEFAULT_I18N_KEYS.errorMessage}
          >
            {list.error}
          </Alert>
        </div>
      );
    }
    return <DataListEmpty />;
  }

  // Sort direction lookup
  function getSortDirection(columnKey: string): 'asc' | 'desc' | null {
    const sort = list.sortState.find((s) => s.column === columnKey);
    return sort?.direction ?? null;
  }

  // Footer cell content for the totals row.
  // A column total (computed sum or authoritative override) is formatted with
  // the column's own number/currency formatting so injected and native totals
  // render identically.
  function renderFooterCell(
    column: { key: string } & ColumnConfig,
  ): ReactNode {
    const total = list.columnTotals[column.key];
    if (total !== undefined) {
      return <Text weight="bold">{formatColumnTotal(total, column)}</Text>;
    }
    if (column.key === list.visibleColumns[0]?.key) {
      return <Text weight="bold">{config.i18n?.totalLabel ?? 'Total'}</Text>;
    }
    return null;
  }

  return (
    <div className={className} data-testid="datalist-table">
      {/* Desktop table view */}
      <div className="hidden md:block" data-testid="desktop-view">
        <Table
          size={config.display?.size ?? 'default'}
          stickyHeader={config.display?.stickyHeader}
          className={config.cssOverrides?.table}
          aria-label={config.display?.title}
          aria-busy={list.isLoading}
        >
          <Table.Head className={config.cssOverrides?.tableHeader}>
            <Table.Row>
              {/* Select-all checkbox */}
              {isSelectable && (
                <Table.HeaderCell width={48}>
                  <Checkbox
                    checked={list.isAllSelected && list.data.length > 0}
                    indeterminate={
                      list.selectedRows.length > 0 && !list.isAllSelected
                    }
                    onChange={() => list.toggleSelectAll()}
                    aria-label="Select all rows"
                  />
                </Table.HeaderCell>
              )}

              {/* Column headers */}
              {list.visibleColumns.map((column) => {
                const isSortable =
                  sortingEnabled && column.sortable !== false;
                const headerCell = (
                  <Table.HeaderCell
                    key={column.key}
                    sortable={isSortable}
                    sortDirection={
                      isSortable ? getSortDirection(column.key) : undefined
                    }
                    onSort={
                      isSortable
                        ? () => list.toggleSort(column.key)
                        : undefined
                    }
                    align={column.align}
                    width={column.width}
                    className={column.headerClassName}
                  >
                    {column.label}
                  </Table.HeaderCell>
                );

                // Wrap in tooltip if headerTooltip is configured
                if (column.headerTooltip) {
                  return (
                    <Tooltip key={column.key} content={column.headerTooltip}>
                      {headerCell}
                    </Tooltip>
                  );
                }
                return headerCell;
              })}

              {/* Action column header */}
              {hasRowActions && (
                <Table.HeaderCell width={100} align="right">
                  Actions
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Head>

          <Table.Body className={config.cssOverrides?.tableBody}>
            {list.data.map((row, index) => (
              <DataListRow
                key={row.id == null ? index : safeString(row.id)}
                row={row}
                index={index}
                columns={list.visibleColumns}
                getCellValue={list.getCellValue}
              />
            ))}
          </Table.Body>

          {/* Totals footer row */}
          {Object.keys(list.columnTotals).length > 0 && (
            <Table.Body data-testid="totals-footer">
              <Table.Row>
                {isSelectable && <Table.Cell />}
                {list.visibleColumns.map((column) => (
                  <Table.Cell key={column.key} align={column.align}>
                    {renderFooterCell(column)}
                  </Table.Cell>
                ))}
                {hasRowActions && <Table.Cell />}
              </Table.Row>
            </Table.Body>
          )}
        </Table>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden flex flex-col gap-3" data-testid="mobile-view">
        {list.data.map((row, index) => (
          <DataListMobileCard
            key={row.id == null ? index : safeString(row.id)}
            row={row}
            columns={list.visibleColumns}
            getCellValue={list.getCellValue}
          />
        ))}
      </div>
    </div>
  );
}

DataListTable.displayName = 'DataListTable';
